import { useEffect, useRef, useState } from "react"
import { mondayService } from "../services/mondayService"
import Tree from "react-d3-tree"
import { utilsService } from "../services/utils.Service"
import { AttentionBox, Dropdown, Icon, Skeleton, Toast, Tooltip } from "monday-ui-react-core"
import iconBoardBlack from '../assets/icons/icon-board.png'
import iconGroupBlack from '../assets/icons/icon-group.png'
import iconItemBlack from '../assets/icons/icon-item.png'
import iconBoardWhite from '../assets/icons/icon-board-white.png'
import iconGroupWhite from '../assets/icons/icon-group-white.png'
import iconItemWhite from '../assets/icons/icon-item-white.png'
import MondayLoader from "../cmps/MondayLoader"

const BOARD_KEY = 'board'
const GROUP_KEY = 'group'
const ITEM_KEY = 'item'

const cacheBoardsData = {}

export default function GroupsTree({ monday, relations, userDomain }) {

    const [treeHead, setTreeHead] = useState()
    const [treeData, setTreeData] = useState(null)
    const [progressColumns, setProgressColumns] = useState(null)
    const [dropdownBoards, setDropdownBoards] = useState([])
    const [treeDepth, setTreeDepth] = useState(0)
    const [treeHeadPos, setTreeHeadPos] = useState({ x: 0, y: 0 })
    const [isLoading, setIsLoading] = useState({ buildTree: true, calculateProgress: false })
    const [errorMsg, setErrorMsg] = useState('')
    const treeRef = useRef()
    useEffect(() => {
        const unsubscribe = monday.listen("settings", (settings) => setProgressColumns(settings.data.progressColumns));
        return () => {
            unsubscribe()
        }
    }, [])

    useEffect(() => {
        if (relations && progressColumns && treeHead?.value) {
            init()
        }
    }, [relations, progressColumns, treeHead])

    useEffect(() => {
        if (relations) {
            loadDropdownBoards()
        }
    }, [relations])


    //center the tree
    useEffect(() => {
        if (treeRef.current) {
            setTreeHeadPos({ x: treeRef.current.clientWidth / 2, y: treeRef.current.clientHeight / 4 })
        }
    }, [treeRef.current])

    // INITIALIZATION
    const init = async () => {
        setIsLoading(prev => ({ ...prev, buildTree: true }))
        await loadBoardData(treeHead.value)
        const tree = onBuildTree(treeHead.value)
   
        setIsLoading(prev => ({ ...prev, buildTree: false }))
        if (progressColumns[treeHead.value]?.[0]) {
            await loadBoardsProgreessColId()
            setIsLoading(prev => ({ ...prev, calculateProgress: true }))
            const updatedTree = await calculateProgress(tree)
            setIsLoading(prev => ({ ...prev, calculateProgress: false }))
            setTreeData(JSON.parse(JSON.stringify(updatedTree)))
        }

    }

    const loadBoardData = async (headBoardId) => {
        try {
            let boardIds = [+headBoardId]
            let itemIds = []

            while (boardIds.length) {
                const mainBoards = relations.filter(relation => boardIds.includes(+relation.mainBoard.board.value));
                const connectColumnIds = mainBoards.map(board => board.mainBoard.connectColumn.value)
                const boardsData = await mondayService.getHeadBoard(boardIds, connectColumnIds, itemIds)
                boardsData.forEach(board => {
                    if (!cacheBoardsData[board.id]) {
                        cacheBoardsData[board.id] = board
                    } else {
                        const items = board.items_page.items.filter(item => !cacheBoardsData[board.id].items_page.items.some(cachesItem => +cachesItem.id === +item.id))
                        cacheBoardsData[board.id].items_page.items.push(...items)
                    }
                });

                const connectedBoardIds = boardsData.map(board => board.columns[0]?.settings_str ? JSON.parse(board.columns[0].settings_str).boardIds : null).flat(1).filter(boardId => !!boardId)

                itemIds = boardsData.reduce((acc, board) => {
                    const itemsIds = board.items_page.items.map(item => item.column_values[0]?.linked_item_ids).flat(1).filter(itemId => !!itemId)
                    return [...acc, ...itemsIds]
                }, [])

                // stop condition if there's no items or no connectedBoardIds 
                boardIds = itemIds.length ? connectedBoardIds : []
            }
            // console.log("🚀 ~ loadBoardData ~ cacheBoardData:", cacheBoardsData)

        } catch (error) {
            console.log("🚀 ~ loadBoardData ~ error:", error)
            setErrorMsg('Failed to fetch the boards data. Please try again.')
        }
    }

    const loadBoardsProgreessColId = async () => {

        if (!treeHead.value) return
        const mainProgressColId = progressColumns[treeHead.value][0]
        let boardsIds = [treeHead.value]
        let progressColsIds = [mainProgressColId]

        while (boardsIds.length && progressColsIds.length) {
            const res = await mondayService.getColumnData(boardsIds, progressColsIds)

            boardsIds = []
            progressColsIds = []
            res.forEach(r => {
                if (!cacheBoardsData[r.id]) return
                cacheBoardsData[r.id].progressCol = r.columns[0]
                if (!!r.columns[0] && r.columns[0].type === "mirror") {
                    const settingsStr = JSON.parse(r.columns[0].settings_str)
                    boardsIds = [...boardsIds, ...Object.keys(settingsStr.displayed_linked_columns)]
                    const newProgressColsIds = Object.values(settingsStr.displayed_linked_columns).filter(val => !!val)
                    progressColsIds = [...progressColsIds, ...newProgressColsIds]
                }
            })
        }
    }

    // TREE BUILD
    const onBuildTree = (boardId) => {
        const currBoard = relations.find(relation => relation.mainBoard.board.value === String(boardId));
        const boardData = cacheBoardsData[boardId]
        const subBoardsIds = currBoard?.subBoards.map(board => board.subBoard)

        const treeData = {
            name: boardData.name,
            attributes: {
                boardId,
                type: BOARD_KEY
            },
            children: boardData.groups.map(group => {
                const items = boardData.items_page.items.filter(item => item.group.id === group.id)

                return {
                    name: group.title,
                    attributes: {
                        board: boardData.name,
                        groupId: group.id,
                        color: group.color,
                        boardId,
                        type: GROUP_KEY
                    },
                    children: items.map(item => buildChildData({ name: item.name, id: item.id, boardId }, boardId, subBoardsIds, group.color, 1))
                }
            })
        }
        setTreeData(treeData)
        return treeData
    }

    // HELPERS

    const getBoardById = (boardId) => {
        return relations.find(relation => relation.mainBoard.board.value === String(boardId));
    };

    const getChildren = (boardIds, itemIds, color, depth) => {
        // return []
        return boardIds.reduce((acc, boardId) => {
            const childrenItems = cacheBoardsData[boardId]?.items_page?.items?.filter(item => itemIds.includes(item.id)) || [];  // Get items in the group
            const currentMainBoard = getBoardById(boardId); // Get the main board
            const subBoardsIds = currentMainBoard?.subBoards?.map(board => board.subBoard) || [];  // Get sub-board IDs
                        // Map each child item with their data

            const children = childrenItems.map(child => buildChildData(child, boardId, subBoardsIds, color, depth));  // Accumulate children items
            return [...acc, ...children];
        }, []);

    };

    const buildChildData = (child, currentBoarrdId, subBoardsIds, color, depth) => {

        setTreeDepth(perv => Math.max(depth, perv))

        const connetedItems = cacheBoardsData[currentBoarrdId].items_page.items.find(item => item.id === child.id).column_values[0]?.linked_item_ids
        return {
            name: child.name,
            attributes: {
                board: cacheBoardsData[currentBoarrdId]?.name,
                color,
                boardId: currentBoarrdId,
                itemId: child.id,
                type: ITEM_KEY
            },
            children: subBoardsIds?.length ? getChildren(subBoardsIds, connetedItems, color, depth + 1) : null // Recursively get children if needed
        };
    };

    const calculateProgress = async (node) => {
        try {
            const progressCol = cacheBoardsData[node.attributes?.boardId]?.progressCol
            if (!node.children || node.children.length === 0 || progressCol?.type === 'formula') {
                let progress = 0
                if (progressCol && progressCol.type === 'formula') {
                    const boardId = node.attributes.boardId
                    const itemId = node.attributes.itemId
                    progress = await mondayService.getFormulaColValue(boardId, itemId, progressCol.id)
                }
                node.attributes.progress = utilsService.toFixedIfNeeded(+progress || 0, 2)
                return node
            }


            let sum = 0;
            // Recursively calculate the value for each child
            for (let i = 0; i < node.children.length; i++) {
                const child = node.children[i]
                const updatedNode = await calculateProgress(child);
                sum += +updatedNode.attributes.progress
            }

            // Calculate the average of all children and assign it to the current node
            node.attributes.progress = utilsService.toFixedIfNeeded(sum / node.children.length, 2)
            return node;
        } catch (error) {
            setErrorMsg('Failed to calculate the progress. Please try again.');
        }
    };

    const loadDropdownBoards = async () => {
        const contextData = await monday.get('context')
        const filterBoardIds = contextData.data.boardIds
        const mainBoards = relations ? relations.map(relation => relation.mainBoard.board) : []
        const dropdownBoards = mainBoards.filter(board => filterBoardIds.includes(+board.value))
        setDropdownBoards(dropdownBoards)
        setTreeHead(dropdownBoards[0])
        if (!dropdownBoards?.length) {
            setIsLoading((prev) => ({ ...prev, buildTree: false }))
        }
    }

    const openSettings = () => {
        monday.execute('openSettings')
    }

    const openNodeDetails = async (node, ev) => {
        const { attributes } = node.data

        switch (attributes.type) {
            case ITEM_KEY:
                monday.execute("openItemCard", { itemId: attributes.itemId });
                break;
            case GROUP_KEY:
                const firstItemId = await mondayService.getFirstItemIdInGroup(attributes.boardId, attributes.groupId)
                monday.execute("openLinkInTab", { url: `https://${userDomain}/boards/${attributes.boardId}/${firstItemId ? 'pulses/' + firstItemId : ""}` });
                break;
            case BOARD_KEY:
                monday.execute("openLinkInTab", { url: `https://${userDomain}/boards/${attributes.boardId}` });
                break;
        }

    }

    // VIEW ELEMENTS

    const NodeElement = ({ nodeDatum, foreignObjectProps, onNodeClick }) => {
        const { attributes } = nodeDatum;
        const nodeName = nodeDatum?.name;
        const backgroundColor = attributes?.color || '';
        const color = backgroundColor ? utilsService.textColorBasedOnBackground(backgroundColor) : '';

        const getIconByType = (type) => {
            switch (type) {
                case BOARD_KEY:
                    return color === "#fff" ? iconBoardWhite : iconBoardBlack;
                case GROUP_KEY:
                    return color === "#fff" ? iconGroupWhite : iconGroupBlack;
                case ITEM_KEY:
                    return color === "#fff" ? iconItemWhite : iconItemBlack;
                default:
                    return null;
            }
        };

        const icon = getIconByType(attributes?.type);
        const headerClass = `node-header ${!backgroundColor ? 'colorless-folder' : ''}`;
        const nodeClass = `node`
        return (
            <g>
                <foreignObject {...foreignObjectProps}>
                    <div className="node-wrapper">

                        <div className={nodeClass} onClick={onNodeClick} style={{ borderColor: backgroundColor }}>
                            <Tooltip content={nodeName}>
                                <div className={headerClass} style={{ backgroundColor, color }}>
                                    {icon && <img src={icon} alt="Node Icon" className="header-icon" />}
                                    <p className="ellipsis header-title">{nodeName}</p>
                                </div>
                            </Tooltip>

                            <div className="node-body">
                                <p className="ellipsis capitalize">{attributes?.board}</p>

                                {isLoading.calculateProgress ? (
                                    <span className="flex auto-center">
                                        Progress: <Skeleton type="text" className="skeleton" width={40} />
                                    </span>
                                ) : (
                                    <p className="ellipsis capitalize">
                                        {isNaN(attributes?.progress) ? '' : `Progress: ${attributes.progress}%`}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </foreignObject>
            </g>
        );
    };

    const renderCustomSvg = () => (
        <svg style={{ position: 'absolute' }}>
            <defs>
                <marker
                    id="arrowhead"
                    markerWidth="12"
                    markerHeight="12"
                    refX="6"
                    refY="6"
                    orient="auto"
                    markerUnits="strokeWidth"
                    className="custom-link"
                >
                    <polyline
                        points="0,0 6,6 12,0"
                        strokeWidth="2"
                        fill="none"
                        transform="rotate(-90, 6, 6)"
                    />
                </marker>
            </defs>
        </svg>
    );


    return <main className='tree-container group-tree' ref={treeRef}>
        <Toast
            className="toast"
            open={!!errorMsg}
            type={Toast.types.NEGATIVE}
            onClose={() => {
                setErrorMsg('')
            }}
            autoHideDuration={5000}
        >
            {errorMsg}
        </Toast>

        {(isLoading.buildTree) ? <MondayLoader /> :
            (dropdownBoards && !dropdownBoards.length) ?
                <AttentionBox
                    title="Unable to load the board connection tree"
                    className="attention-box"
                >
                    <p style={{ textAlign: 'start' }}>Please make sure to either:</p>
                    <p style={{ textAlign: 'start' }}>• Connect your main board (e.g. Company Objectives) to this dashboard</p>
                    <p style={{ textAlign: 'start' }}>• Open this view directly from the main board's context, using a blank view within the main board</p>



                </AttentionBox>
                : <>
                    {dropdownBoards?.length > 1 && <div className="select-div">
                        <Dropdown
                            className="select"
                            placeholder="Select the head of the tree"
                            value={treeHead}
                            options={dropdownBoards}
                            onChange={setTreeHead}
                            isLoading={!relations}
                        />
                    </div>}



                    {!progressColumns?.[treeHead.value]?.[0] && <AttentionBox
                        className="attention-box"
                    >
                        To enjoy the full experience and track progress, please set the Progress column in the <a onClick={openSettings}>settings</a>.
                    </AttentionBox>}
                    {treeDepth > 3 && <AttentionBox
                        className="attention-box"
                    >
                        Currently, progress can only be viewed up to the third board level.
                    </AttentionBox>}

                    {!!treeData && <>
                        {renderCustomSvg() /* necessary for the arrow in the of the line*/}

                        < Tree
                            data={treeData}
                            pathFunc={'step'}
                            orientation='vertical'
                            zoom={0.7}
                            svgClassName="custom-tree"
                            collapsible={true}
                            centeringTransitionDuration={200}
                            translate={treeHeadPos}
                            renderCustomNodeElement={(props) => <NodeElement {...props}
                                foreignObjectProps={{
                                    width: 350, height: 250, x: -175, y: 0
                                }} />
                            }
                            nodeSize={{ x: 450, y: 400 }}
                            pathClassFunc={() => 'custom-link'}
                            onNodeClick={openNodeDetails}
                        />
                    </>}
                </>}

    </main>
}